import { Route, Routes } from 'react-router-dom';
import { loadScreenRegistrySync } from '../screens/registry.js';
import type { AnyScreenDefinition } from '../screens/types.js';
import { AppShell } from '../shell/AppShell.js';
import { NotFound } from './NotFound.js';

/** Does this screen render before there is a session? */
export function isUnauthenticated(screen: AnyScreenDefinition): boolean {
  return screen.reachedBy.some(
    (entrance) => entrance.kind === 'unauthenticated' || entrance.kind === 'public',
  );
}

/**
 * One screen, mounted the way the application mounts it.
 *
 * The router and the test harness both call this. Two functions that each know
 * how to mount a screen would eventually disagree, and the one that was wrong
 * would be the one nobody was testing - a screen audited outside the shell it
 * actually lives in proves very little about the screen people see.
 */
export function screenElement(screen: AnyScreenDefinition): JSX.Element {
  const Screen = screen.component;
  if (isUnauthenticated(screen) || screen.chrome === 'bare') return <Screen />;
  return (
    <AppShell>
      <Screen />
    </AppShell>
  );
}

/**
 * Routes come from the screen registry, never from a hand-written list.
 *
 * A screen that exists but was left out of the router is unreachable, and a
 * router entry pointing at a screen that no longer exists is a broken link.
 * Deriving both from one registry removes the possibility of either.
 *
 * Screens reached while signed out render bare; everything else renders inside
 * the shell, which is what reads the session.
 */
export function AppRoutes(): JSX.Element {
  const { screens } = loadScreenRegistrySync();
  return (
    <Routes>
      {screens
        .filter((screen) => screen.surface === 'page')
        .map((screen) => (
          <Route key={String(screen.id)} path={screen.route} element={screenElement(screen)} />
        ))}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
